import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { locale } from '@fortune-sheet/core';
import type { WorkbookInstance } from '@fortune-sheet/react';
import { getInternals, isEditingInContext } from './workbookInternals';
import { isNativeEditableElement, isGridKeyTarget } from './shortcutKeymap';
import { TOOLBAR_SELECTOR, TOOLBAR_COMBO_POPUP_SELECTOR, TOOLBAR_MORE_CONTAINER_SELECTOR } from './selectors';
import {
    ALL_TOOLBAR_KEYS,
    resolveToolbarTargets,
    resolveMenuOptions,
    filterByPrefix,
    altTapReducer,
    canArmKeyTips,
    INITIAL_ALT_TAP_STATE,
    type ToolbarTarget,
    type AltTapState,
} from './keyTips';

// Mirrors `fortuneSheetSetup.ts`'s `readEnLocale` pattern: `locale()` is typed
// as taking a full Context and returning a large locale object; we only ever
// read a couple of its string leaves, so narrow both sides here too.
type ToolbarLocale = { toolbar?: Record<string, string>; findAndReplace?: Record<string, string> };
const readToolbarLocale = locale as unknown as (ctx: { lang: string }) => ToolbarLocale;

// Fortune's `getToolbarItem` (react:5680-6671) overrides its default
// `toolbar[name]` tooltip lookup for these — mirrored here so KeyTips matches
// the SAME `data-tips` text the button actually renders. `locationCondition`
// is the one exception that lives in a different locale namespace entirely.
const TOOLTIP_OVERRIDES: Record<string, (l: ToolbarLocale) => string | undefined> = {
    'horizontal-align': (l) => l.toolbar?.horizontalAlign,
    'vertical-align': (l) => l.toolbar?.verticalAlign,
    conditionFormat: (l) => l.toolbar?.conditionalFormat,
    image: (l) => l.toolbar?.insertImage,
    'quick-formula': (l) => l.toolbar?.autoSum,
    'text-wrap': (l) => l.toolbar?.textWrap,
    'text-rotation': (l) => l.toolbar?.textRotate,
    filter: (l) => l.toolbar?.sortAndFilter,
    locationCondition: (l) => l.findAndReplace?.location,
    more: (l) => l.toolbar?.toolMore,
};

// Our own custom toolbar buttons (`useColumnAutofit.tsx`, `FormatCellsDialog.tsx`)
// supply their tooltip literally rather than through the vendor locale.
const CUSTOM_TOOLTIPS: Record<string, string> = {
    'fit-columns': 'Fit column width to content',
    'format-cells': 'Format cells…',
};

/** Builds the `{key: tooltip}` map `resolveToolbarTargets` matches `data-tips`
 *  against, across every toolbar control. A key with no resolvable tooltip
 *  (confirmed for 'search'/Find and 'link': neither has a `toolbar.<name>`
 *  locale entry, nor an override above) is simply omitted —
 *  `resolveToolbarTargets`'s icon-based fallback still finds it. */
const buildTooltipsByKey = (): Record<string, string> => {
    const tooltips: Record<string, string> = { ...CUSTOM_TOOLTIPS };
    try {
        const en = readToolbarLocale({ lang: 'en' });
        ALL_TOOLBAR_KEYS.forEach((key) => {
            if (key in tooltips) return;
            const tooltip = (TOOLTIP_OVERRIDES[key] ?? ((l: ToolbarLocale) => l.toolbar?.[key]))(en);
            if (tooltip) tooltips[key] = tooltip;
        });
    } catch {
        // Locale shape changed in a library upgrade — non-fatal; the icon
        // fallback in resolveToolbarTargets still covers every default button.
    }
    return tooltips;
};

const OPEN_ANNOUNCEMENT = 'Toolbar shortcuts on. Type a letter, or press Esc to cancel.';
const MENU_ANNOUNCEMENT = 'Type a command letter.';

const DIGIT_CODE_TO_CHAR: Record<string, string> = {
    Digit0: '0',
    Digit1: '1',
    Digit2: '2',
    Digit3: '3',
    Digit4: '4',
    Digit5: '5',
    Digit6: '6',
    Digit7: '7',
    Digit8: '8',
    Digit9: '9',
    Period: '.',
};

/** Letters filter by `e.key` (upper-cased); digits/punctuation filter by
 *  `e.code` — the physical key, unaffected by layout/modifier remaps. */
const charFromKeydown = (e: KeyboardEvent): string | null => {
    if (/^[a-zA-Z]$/.test(e.key)) return e.key.toUpperCase();
    return DIGIT_CODE_TO_CHAR[e.code] ?? null;
};

/**
 * `el.click()` alone synthesizes only a `click` event with no coordinates —
 * confirmed empirically against the patched real `<Workbook>` that Fortune's
 * own toolbar actions (e.g. Bold) then read a RESET, degenerate selection
 * (`{row:[0,null]}`) instead of the cell that was actually selected, so the
 * action silently applies to nothing. A full `mousedown`+`mouseup`+`click`
 * sequence with the target's own on-screen coordinates mirrors what a real
 * mouse click actually dispatches and keeps the selection intact.
 */
const simulateClick = (el: HTMLElement): void => {
    const rect = el.getBoundingClientRect();
    const clientX = rect.left + rect.width / 2;
    const clientY = rect.top + rect.height / 2;
    const fire = (type: string) =>
        el.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window, clientX, clientY }));
    fire('mousedown');
    fire('mouseup');
    fire('click');
};

export interface UseKeyTipsArgs {
    open: boolean;
    /** The grid wrapper (`[data-aica-sheet-editor]`) — the toolbar is a
     *  descendant of it, and it is also the "inside the editor" target gate. */
    wrapperRef: RefObject<HTMLElement>;
    workbookRef: RefObject<WorkbookInstance>;
    historyOpen: boolean;
    dialogOpen: boolean;
    focusCellInput: () => void;
}

export interface UseKeyTipsReturn {
    keytipsOpen: boolean;
    targets: ToolbarTarget[];
    announce: string;
    /** Esc handling (rule 0, `escapePolicy.ts`): closes one STAGE (nested menu
     *  → the root badge set → off). Never calls the drawer's `onClose`. */
    closeLevel: () => void;
}

type Stage = 'root' | 'menu';

interface ViewModel {
    isOpen: boolean;
    targets: ToolbarTarget[];
    announce: string;
}

const CLOSED_VIEW: ViewModel = { isOpen: false, targets: [], announce: '' };
const CLOSE_ANNOUNCEMENT = 'Toolbar shortcuts off.';
const CLOSE_ANNOUNCE_DECAY_MS = 1500;

/**
 * Lane K: a modal document-capture keydown/keyup/mousedown/wheel/blur
 * listener that arms on a bare Alt keydown and opens on the matching keyup
 * (a "tap"), then consumes every key until a KeyTip is picked or the overlay
 * is dismissed. Registered only while `open`, mirroring
 * `useSheetKeyboardShortcuts.ts`'s Lane B listener.
 *
 * Single stage: a tap badges every toolbar control at once (`stage: 'root'`);
 * picking a combo or the More button opens a further nested level
 * (`stage: 'menu'`) badging that popup's own options, or — for More — the
 * real controls hidden inside it at their real positions. Esc steps back
 * exactly one stage.
 */
export const useKeyTips = ({
    open,
    wrapperRef,
    workbookRef,
    historyOpen,
    dialogOpen,
    focusCellInput,
}: UseKeyTipsArgs): UseKeyTipsReturn => {
    const [view, setView] = useState<ViewModel>(CLOSED_VIEW);
    const tooltipsByKey = useRef(buildTooltipsByKey()).current;

    const latest = { open, wrapperRef, workbookRef, historyOpen, dialogOpen, focusCellInput };
    const latestRef = useRef(latest);
    latestRef.current = latest;

    // Mirrors `view.isOpen` synchronously so the document listener (registered
    // once per `open` toggle, not on every render) never reads a stale value.
    const isOpenRef = useRef(false);
    const applyView = useCallback((next: ViewModel) => {
        isOpenRef.current = next.isOpen;
        setView(next);
    }, []);

    const altStateRef = useRef<AltTapState>(INITIAL_ALT_TAP_STATE);
    const stageRef = useRef<Stage>('root');
    const rootTargetsRef = useRef<ToolbarTarget[]>([]);
    const menuTargetsRef = useRef<ToolbarTarget[] | null>(null);
    const typedRef = useRef('');
    // `simulateClick`'s own synthetic mousedown would otherwise reach our own
    // document-capture `onMousedown` below and close/disarm mid-activation —
    // this flag tells it to ignore that one, self-inflicted mousedown.
    const isActivatingRef = useRef(false);
    // Set while an overflow-resolved activation is using Fortune's own
    // "More" panel (holds the More button itself, whether we opened it or it
    // was already open) — `close()` re-clicks it to shut the panel, since
    // the real click landed INSIDE the container, never on its own
    // useOutsideClick backdrop.
    const overflowMoreButtonRef = useRef<HTMLElement | null>(null);
    // The live region must stay mounted and announce the close, unlike
    // every other transition (which is silent) — a separate timer decays it
    // back to empty after CLOSE_ANNOUNCE_DECAY_MS, cleared on the next
    // open/close so a quick reopen never has its "on" announcement wiped by a
    // stale "off" timer.
    const closeAnnounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearCloseAnnounceTimer = useCallback(() => {
        if (closeAnnounceTimerRef.current) {
            clearTimeout(closeAnnounceTimerRef.current);
            closeAnnounceTimerRef.current = null;
        }
    }, []);
    useEffect(() => {
        return clearCloseAnnounceTimer; // unmount only
    }, [clearCloseAnnounceTimer]);

    const close = useCallback(() => {
        // A pending overflow activation's own More panel is still showing —
        // re-click the SAME button Fortune's own toggle uses (only if the
        // panel is genuinely still present; some other means may already
        // have closed it) rather than trying to synthesize its own
        // useOutsideClick backdrop mousedown.
        const moreBtn = overflowMoreButtonRef.current;
        overflowMoreButtonRef.current = null;
        if (moreBtn && latestRef.current.wrapperRef.current?.querySelector(TOOLBAR_MORE_CONTAINER_SELECTOR)) {
            isActivatingRef.current = true;
            simulateClick(moreBtn);
            isActivatingRef.current = false;
        }
        altStateRef.current = INITIAL_ALT_TAP_STATE;
        stageRef.current = 'root';
        menuTargetsRef.current = null;
        typedRef.current = '';
        clearCloseAnnounceTimer();
        applyView({ isOpen: false, targets: [], announce: CLOSE_ANNOUNCEMENT });
        closeAnnounceTimerRef.current = setTimeout(() => applyView(CLOSED_VIEW), CLOSE_ANNOUNCE_DECAY_MS);
        latestRef.current.focusCellInput();
    }, [applyView, clearCloseAnnounceTimer]);

    const showRoot = useCallback(() => {
        stageRef.current = 'root';
        menuTargetsRef.current = null;
        typedRef.current = '';
        applyView({ isOpen: true, targets: rootTargetsRef.current, announce: OPEN_ANNOUNCEMENT });
    }, [applyView]);

    const closeLevel = useCallback(() => {
        if (stageRef.current === 'menu') {
            showRoot();
            return;
        }
        close();
    }, [close, showRoot]);

    /** Resolves and shows every toolbar control's badge against the live DOM
     *  — closes entirely if nothing resolves (a mounted toolbar always has Find, so
     *  this shouldn't happen in practice, but mirrors `openMenu`'s own
     *  empty-set guard). */
    const openKeyTips = useCallback(() => {
        const toolbarEl = latestRef.current.wrapperRef.current?.querySelector<HTMLElement>(TOOLBAR_SELECTOR);
        if (!toolbarEl) return;
        clearCloseAnnounceTimer();
        const targets = resolveToolbarTargets(toolbarEl, tooltipsByKey);
        if (targets.length === 0) {
            close();
            return;
        }
        rootTargetsRef.current = targets;
        showRoot();
    }, [clearCloseAnnounceTimer, close, showRoot, tooltipsByKey]);

    /** Opens a nested level (a combo's option list, or the real controls
     *  hidden inside an opened More panel), or closes entirely when the
     *  probe below found nothing to show. */
    const openMenu = useCallback(
        (nested: ToolbarTarget[]) => {
            if (nested.length === 0) {
                close();
                return;
            }
            menuTargetsRef.current = nested;
            stageRef.current = 'menu';
            typedRef.current = '';
            applyView({ isOpen: true, targets: nested, announce: MENU_ANNOUNCEMENT });
        },
        [close, applyView],
    );

    // Clicking a combo opener may reveal a popup 1-2 frames later (Combo's
    // own `useLayoutEffect`, react:4223-4240) — probed rather than assumed
    // synchronous. A `more`/`overflow` target's `el` is the More toggle
    // button itself — clicking it reveals `.fortune-toolbar-more-container`,
    // which Fortune populates with the SAME `getToolbarItem` markup as the
    // main row, so re-running `resolveToolbarTargets` against it (once open)
    // finds the real controls at their real positions.
    const activate = useCallback(
        (target: ToolbarTarget) => {
            if (target.kind === 'more' || target.kind === 'overflow') {
                const wrapperEl = latestRef.current.wrapperRef.current;
                // Fortune's own More button is a plain toggle — clicking it
                // while the panel is ALREADY open (e.g. a stray leftover, or
                // simply still settling from a just-finished prior overflow
                // activation) would close it instead of opening it. Only
                // click when it's genuinely not open yet; either way, this
                // activation now owns closing it once it concludes.
                const alreadyOpen = Boolean(wrapperEl?.querySelector(TOOLBAR_MORE_CONTAINER_SELECTOR));
                if (!alreadyOpen) {
                    isActivatingRef.current = true;
                    simulateClick(target.el);
                    isActivatingRef.current = false;
                }
                overflowMoreButtonRef.current = target.el;
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        const moreContainer = wrapperEl?.querySelector<HTMLElement>(TOOLBAR_MORE_CONTAINER_SELECTOR);
                        const panelTargets = moreContainer ? resolveToolbarTargets(moreContainer, tooltipsByKey) : [];
                        if (target.kind === 'more') {
                            // The user asked to browse the panel itself
                            // (typed its own code) — badge its real controls
                            // at their real positions; Esc steps back to root.
                            openMenu(panelTargets);
                            return;
                        }
                        // The user typed a hidden control's own code directly
                        // — activate it immediately, same as any other target.
                        const real = panelTargets.find((t) => t.key === target.key);
                        if (real) activate(real);
                        else close();
                    });
                });
                return;
            }
            isActivatingRef.current = true;
            simulateClick(target.el);
            isActivatingRef.current = false;
            // Only a combo opener can reveal a further nested level — a plain
            // button and a menu option (a combo popup's own row) both just
            // close immediately.
            if (target.kind !== 'combo-open') {
                close();
                return;
            }
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    // A combo's options get no tooltip of their own — align's
                    // options get Excel's own mnemonics (L/C/R/T/M/B, matched
                    // by icon); every other combo falls back to DOM-order
                    // numbering (resolveMenuOptions).
                    const popup = target.el
                        .closest('.fortune-toobar-combo-container')
                        ?.querySelector<HTMLElement>(TOOLBAR_COMBO_POPUP_SELECTOR);
                    const optionEls = popup
                        ? Array.from(popup.querySelectorAll<HTMLElement>('.fortune-toolbar-select-option')).filter(
                              (el) => el.offsetParent !== null,
                          )
                        : [];
                    openMenu(resolveMenuOptions(optionEls));
                });
            });
        },
        [close, openMenu, tooltipsByKey],
    );

    /** The `canArmKeyTips` gate args for a bare-Alt keydown target — reads
     *  the LATEST render's workbook/dialog/history/wrapper state via
     *  `latestRef` since this is called from a listener registered once per
     *  `open` toggle, not re-bound on every render. */
    const computeGateArgs = (target: EventTarget | null) => {
        const { workbookRef: wbRef, dialogOpen: dlgOpen, historyOpen: histOpen, wrapperRef: wRef, open: isOpenArg } = latestRef.current;
        const internals = getInternals(wbRef.current);
        return {
            open: isOpenArg,
            editing: isEditingInContext(internals?.getContext()),
            // The grid's own contentEditable cell input and
            // formula bar are "native editable" too (isNativeEditableElement
            // is true for any isContentEditable element), but they must NOT
            // block arming — they are exactly Lane A's own grid targets
            // (shortcutKeymap.ts's isGridKeyTarget). Only a REAL native
            // editable outside the grid (Name Box, Find, tab rename, a
            // dialog input) should refuse the tap.
            nativeEditableTarget: target instanceof Element && isNativeEditableElement(target) && !isGridKeyTarget(target),
            dialogOpen: dlgOpen,
            historyOpen: histOpen,
            targetInsideEditor: target === document.body || Boolean(target instanceof Node && wRef.current?.contains(target)),
        };
    };

    /** While closed: arms/disarms on the bare-Alt tap sequence. While open:
     *  consumes every key — Esc steps back one stage, a second Alt tap
     *  closes, and a printable char narrows (or picks) the current targets
     *  by prefix. */
    const onKeydown = (e: KeyboardEvent): void => {
        if (!isOpenRef.current) {
            const isBareAlt = e.key === 'Alt' && !e.repeat && !e.ctrlKey && !e.metaKey && !e.shiftKey;
            if (isBareAlt && !canArmKeyTips(computeGateArgs(e.target))) return; // gated — leave untouched
            const { state, action } = altTapReducer(altStateRef.current, {
                type: 'keydown',
                key: e.key,
                repeat: e.repeat,
                ctrlKey: e.ctrlKey,
                metaKey: e.metaKey,
                shiftKey: e.shiftKey,
            });
            altStateRef.current = state;
            if (action === 'arm') e.preventDefault();
            return;
        }

        // Open: consume everything so the grid/library never sees it.
        e.preventDefault();
        e.stopImmediatePropagation();
        if (e.key === 'Escape') {
            closeLevel();
            return;
        }
        if (e.key === 'Alt' && !e.repeat) {
            close(); // a second full tap closes
            return;
        }
        const char = charFromKeydown(e);
        if (!char) return;
        const typed = typedRef.current + char;
        const stage = stageRef.current;
        const matches = filterByPrefix(getActiveTargets(), typed);
        if (matches.length === 0) return; // no matching prefix — ignore, stay open
        if (matches.length === 1 && matches[0].code === typed) {
            typedRef.current = '';
            activate(matches[0]);
            return;
        }
        typedRef.current = typed;
        applyView({ isOpen: true, targets: matches, announce: stage === 'root' ? OPEN_ANNOUNCEMENT : MENU_ANNOUNCEMENT });
    };

    /** While closed: completes the bare-Alt tap (arm on keydown, open on the
     *  matching keyup) — a no-op once already open. */
    const onKeyup = (e: KeyboardEvent): void => {
        if (isOpenRef.current) return;
        const { state, action } = altTapReducer(altStateRef.current, { type: 'keyup', key: e.key });
        altStateRef.current = state;
        if (action === 'toggle') {
            e.preventDefault();
            openKeyTips();
        }
    };

    // While ARMED (closed), only another keydown/mousedown/blur
    // disarms — a wheel event does not. While OPEN, all four close it.
    const onMousedown = (): void => {
        if (isActivatingRef.current) return; // our own synthetic click, not a dismiss
        if (isOpenRef.current) close();
        else altStateRef.current = altTapReducer(altStateRef.current, { type: 'mousedown' }).state;
    };
    const onBlur = (): void => {
        if (isOpenRef.current) close();
        else altStateRef.current = altTapReducer(altStateRef.current, { type: 'blur' }).state;
    };
    const onWheel = (): void => {
        if (isOpenRef.current) close();
    };
    // The currently-showing badge set — the nested menu's own options while
    // in that stage, otherwise the root (every-control) list. Shared by the
    // keydown handler's prefix match and the resize safety net below.
    const getActiveTargets = (): ToolbarTarget[] => (stageRef.current === 'menu' ? menuTargetsRef.current ?? [] : rootTargetsRef.current);
    // The toolbar re-wrapping mid-session (a window resize) can move a
    // control from the main row into "More" or back — re-resolving the whole
    // tree on every resize is unnecessary churn; this only guards the rare
    // case where EVERY currently-active target's element was actually
    // removed from the DOM (Fortune re-rendered its toolbar), which would
    // otherwise leave KeyTips open with badges pointing at nothing.
    const onResize = (): void => {
        if (!isOpenRef.current) return;
        const activeTargets = getActiveTargets();
        if (activeTargets.length > 0 && activeTargets.every((t) => !t.el.isConnected)) close();
    };

    useEffect(() => {
        if (!open) return undefined;
        document.addEventListener('keydown', onKeydown, true);
        document.addEventListener('keyup', onKeyup, true);
        document.addEventListener('mousedown', onMousedown, true);
        document.addEventListener('wheel', onWheel, true);
        window.addEventListener('blur', onBlur);
        window.addEventListener('resize', onResize);
        return () => {
            document.removeEventListener('keydown', onKeydown, true);
            document.removeEventListener('keyup', onKeyup, true);
            document.removeEventListener('mousedown', onMousedown, true);
            document.removeEventListener('wheel', onWheel, true);
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('resize', onResize);
        };
        // onKeydown/onKeyup/onMousedown/onBlur/onWheel/onResize are recreated
        // every render but always close over the SAME stable callbacks below
        // (each one's own dependency chain bottoms out in a `[]`-deps
        // useCallback or a ref) — omitted here, same as
        // useSheetKeyboardShortcuts.ts's own handler/handleEscape, so the
        // listener registers once per `open` toggle instead of every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, close, closeLevel, openKeyTips, activate]);

    return { keytipsOpen: view.isOpen, targets: view.targets, announce: view.announce, closeLevel };
};
